import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import { cn } from '@/utils/cn'
import type {
  AdminApplicationItem,
  Alert,
  AlertSeverity,
  AlertStatus,
} from '@/types/controlCenter'
import { labelClass, inputClass } from './shared/modalStyles'
import { cap } from '../utils/format'
import { SEVERITY_OPTIONS } from '../Alerts/constants'
import { SEVERITY_MAP_INVERSE } from '../utils/mappers'
import { QUERY_KEYS } from '../utils/queryKeys'
import ClientsDropdown from './ClientsDropdown'
import ExpiryPicker from './ExpiryPicker'
import ModalShell from './shared/ModalShell'

interface EditAlertModalProps {
  alert?: Alert | null
  applications?: AdminApplicationItem[]
  clientNameToId?: Record<string, number>
  onClose: () => void
}

function buildEndDate(date: Date | undefined, time: string): string | null {
  if (!date) return null
  const [h, m] = time.split(':').map(Number)
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null
  const d = new Date(date)
  d.setHours(h, m, 0, 0)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

const EditAlertModal = ({
  alert,
  applications = [],
  clientNameToId = {},
  onClose,
}: EditAlertModalProps) => {
  const queryClient = useQueryClient()
  const isCreate = !alert

  const [name, setName] = useState(alert?.name ?? '')
  const [description, setDescription] = useState(alert?.description ?? '')
  // siteWide=true means the alert applies to every page (path === '/').
  // Kept separate so the intent is explicit rather than implied by an empty field.
  const [siteWide, setSiteWide] = useState(alert?.url === '/')
  const [url, setUrl] = useState(alert?.url === '/' ? '' : (alert?.url ?? ''))
  // Store the application id directly so lookups can't fail or resolve to a wrong entry.
  const [applicationId, setApplicationId] = useState<number | null>(
    alert?.applicationId ?? applications[0]?.id ?? null,
  )
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(
    new Set(alert?.clientIds ?? []),
  )
  const supportsClient =
    applications.find((a) => a.id === applicationId)?.client_supported ?? false
  const [severity, setSeverity] = useState<AlertSeverity>(
    alert?.severity ?? 'info',
  )
  const [status, setStatus] = useState<AlertStatus>(alert?.status ?? 'active')
  const [dismissible, setDismissible] = useState(alert?.dismissible ?? true)
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(
    alert?.endDate ? new Date(alert.endDate) : undefined,
  )
  const [expiryTime, setExpiryTime] = useState(
    alert?.endDate ? format(new Date(alert.endDate), 'HH:mm') : '23:59',
  )
  // Applications may load after the modal opens (async query).
  // If we don't have an id yet, pick the target app or the first in the list.
  useEffect(() => {
    if (applicationId === null && applications.length > 0)
      setApplicationId(alert?.applicationId ?? applications[0].id)
  }, [applications, applicationId, alert?.applicationId])

  const mutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.alerts,
      })
      onClose()
    },
  })

  const endDate = buildEndDate(expiryDate, expiryTime)

  const isFormValid =
    name.trim() !== '' &&
    description.trim() !== '' &&
    applicationId !== null &&
    (siteWide || url.trim() !== '') &&
    (!supportsClient || selectedClientIds.size > 0) &&
    (!expiryDate || (endDate !== null && new Date(endDate) > new Date()))

  const handleSave = () => {
    mutation.mutate({
      method: isCreate ? 'post' : 'put',
      url: isCreate
        ? `${CONTROL_CENTRE_API_DOMAIN}/admin/alerts`
        : `${CONTROL_CENTRE_API_DOMAIN}/admin/alerts/${alert.id}`,
      data: {
        name: name.trim(),
        message: description.trim(),
        path: siteWide ? '/' : url.trim(),
        active: status === 'active',
        type: SEVERITY_MAP_INVERSE[severity],
        dismissible,
        application_id: applicationId,
        client_ids: Array.from(selectedClientIds),
        start_date: null,
        end_date: endDate,
      },
    })
  }

  return (
    <ModalShell
      title={isCreate ? 'Create Alert' : 'Edit Alert'}
      subtitle={!isCreate ? alert.name : undefined}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
        className='max-h-[75vh] space-y-5 overflow-y-auto px-8 py-6'
      >
        <div>
          <label htmlFor='alert-name' className={labelClass}>
            Name *
          </label>
          <input
            id='alert-name'
            required
            aria-required='true'
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor='alert-message' className={labelClass}>
            Message *
          </label>
          <textarea
            id='alert-message'
            required
            aria-required='true'
            className={cn(inputClass, 'resize-none')}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label htmlFor='alert-application' className={labelClass}>
              Application *
            </label>
            <select
              id='alert-application'
              required
              aria-required='true'
              className={inputClass}
              value={applicationId ?? ''}
              onChange={(e) => {
                const id = Number(e.target.value)
                setApplicationId(id)
                const nextApp = applications.find((a) => a.id === id)
                if (!nextApp?.client_supported) setSelectedClientIds(new Set())
              }}
            >
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
          <ClientsDropdown
            clientNameToId={clientNameToId}
            selectedIds={selectedClientIds}
            onChange={setSelectedClientIds}
            disabled={!supportsClient}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label htmlFor='alert-path' className={labelClass}>
              Path *
            </label>
            <input
              id='alert-path'
              required={!siteWide}
              aria-required={!siteWide}
              disabled={siteWide}
              className={cn(
                inputClass,
                siteWide && 'cursor-not-allowed opacity-40',
              )}
              placeholder='e.g. /peacock/dashboard'
              value={siteWide ? '/' : url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <label className='text-text-tertiary mt-1.5 flex cursor-pointer items-center gap-2 text-xs'>
              <input
                type='checkbox'
                checked={siteWide}
                onChange={(e) => {
                  setSiteWide(e.target.checked)
                  if (e.target.checked) setUrl('')
                }}
                className='accent-violet-500'
              />
              Show on all pages
            </label>
          </div>
          <ExpiryPicker
            date={expiryDate}
            time={expiryTime}
            onDateChange={setExpiryDate}
            onTimeChange={setExpiryTime}
          />
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label htmlFor='alert-severity' className={labelClass}>
              Severity *
            </label>
            <select
              id='alert-severity'
              required
              aria-required='true'
              className={inputClass}
              value={severity}
              onChange={(e) => setSeverity(e.target.value as AlertSeverity)}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {cap(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor='alert-status' className={labelClass}>
              Status *
            </label>
            <select
              id='alert-status'
              required
              aria-required='true'
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as AlertStatus)}
            >
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
            </select>
          </div>
          <div>
            <label htmlFor='alert-dismissible' className={labelClass}>
              Dismissible *
            </label>
            <select
              id='alert-dismissible'
              required
              aria-required='true'
              className={inputClass}
              value={dismissible ? 'true' : 'false'}
              onChange={(e) => setDismissible(e.target.value === 'true')}
            >
              <option value='true'>Yes</option>
              <option value='false'>No</option>
            </select>
          </div>
        </div>

        {mutation.error && (
          <p className='text-sm text-red-400'>{mutation.error.message}</p>
        )}

        <div className='border-border-main flex items-center justify-end gap-3 border-t px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            className='border-border-main text-text-secondary hover:bg-neutral-dimmed rounded-lg border px-4 py-2 text-sm'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={!isFormValid || mutation.isPending}
            className='rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40'
          >
            {mutation.isPending
              ? 'Saving…'
              : isCreate
                ? 'Create Alert'
                : 'Save'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

export default EditAlertModal
